import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/** Duração/curva compartilhada — FLIP (CSS transition) e saída (Web Animations)
 *  derivam destes mesmos números, pra tudo terminar junto na mesma curva. */
const VD_DURATION = 450;
const VD_EASING = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
const VD_TIMING = `${VD_DURATION}ms ${VD_EASING}`;
const VD_TRANSITION = `transform ${VD_TIMING}`;
/** Deslocamento "para fora" da entrada/saída de borda (px). */
const VD_EDGE_SHIFT = 40;

/**
 * Carrossel "coverflow" de vídeos verticais (depoimentos em vídeo).
 *
 * Por que bespoke e não <slideshow-component>: o loop infinito nativo
 * (assets/slideshow.js) só roda quando há 1 slide visível; aqui mostramos 5
 * cards ao mesmo tempo (centro maior), então precisamos do nosso próprio
 * posicionamento e da lógica de tocar SÓ o card central.
 *
 * Layout: track flex com justify-content:center. Cada card recebe:
 *   - data-distance (0/1/2) -> dirige a LARGURA via CSS (centro maior).
 *   - style.order = distância ASSINADA no anel -> reposiciona o anel de forma
 *     simétrica em torno do centro. Cards fora da janela (|delta|>2) somem via
 *     CSS (display:none) para não consumir gap, mantendo o ativo no centro.
 *
 * Mídia: o card central toca MUDO + loop; clicar nele alterna o som
 * (tap-to-unmute). Ao sair do centro o vídeo re-muta e pausa. Os vídeos só
 * tocam quando a seção está visível (IntersectionObserver) e a aba ativa.
 *
 * FIX 2026-07-03 (autoplay bloqueado no Safari mobile): `video.play()`
 * programático pode ser rejeitado no iOS Safari mesmo com o vídeo mudo —
 * acontece sobretudo com Modo de Baixo Consumo / Modo de Dados Reduzidos
 * ativos, que bloqueiam autoplay iniciado por script (mas não bloqueiam
 * play() disparado dentro de um gesto real do usuário). Antes, a rejeição
 * era só engolida (`.catch(() => {})`) e o vídeo ficava parado pro resto da
 * visita, sem tentar de novo. Agora: guardamos quais vídeos tiveram o
 * autoplay bloqueado e tentamos tocá-los de novo no PRÓXIMO toque do usuário
 * no carrossel (qualquer pointerup já conta como gesto explícito). O poster
 * de fallback (corrigido na section .liquid) garante que, mesmo enquanto o
 * vídeo não toca, sempre haja uma imagem estática visível — nunca mais um
 * retângulo em branco.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track
 * @property {HTMLElement[]} cards
 * @property {HTMLButtonElement[]} [dotBtns]
 *
 * @extends {Component<Refs>}
 */
class VideoDepoimentos extends Component {
  requiredRefs = ['track', 'cards'];

  /** Índice lógico do card central. Começa em 0 (dot 1 ativo, igual ao Figma). */
  #active = 0;
  /** Total de cards. */
  #count = 0;
  /** Seção visível no viewport. */
  #inView = false;
  /** X inicial de um gesto de arrasto. */
  #pointerStartX = null;
  /** Suprime o clique sintético que segue um swipe. */
  #suppressClick = false;
  /** @type {IntersectionObserver | undefined} */
  #io;
  /** Cards com animação de SAÍDA em andamento (fora do fluxo, fazendo fade-out). @type {Set<HTMLElement>} */
  #exiting = new Set();
  /** Animação WAA por card que está saindo (p/ cancelar em caso de interrupção). @type {Map<HTMLElement, Animation>} */
  #exitAnims = new Map();
  /** Vídeos cujo autoplay foi rejeitado pelo navegador (ex.: iOS Safari em Modo
   *  de Baixo Consumo) e aguardam um gesto real do usuário p/ tentar de novo.
   *  @type {Set<HTMLVideoElement>} */
  #autoplayBlocked = new Set();
  /** Evita logar o mesmo aviso de autoplay bloqueado repetidamente. */
  #autoplayWarned = false;

  connectedCallback() {
    super.connectedCallback();
    this.#count = this.refs.cards.length;
    if (!this.#count) return;
    this.#active = 0;
    this.#layout(false);
    // Toca/pausa conforme a seção entra/sai do viewport.
    this.#io = new IntersectionObserver(
      ([entry]) => {
        this.#inView = entry.isIntersecting;
        this.#syncMedia();
      },
      { threshold: 0.4 }
    );
    this.#io.observe(this);
    document.addEventListener('visibilitychange', this.#onVisibility);
    this.refs.track.addEventListener('pointerdown', this.#onPointerDown);
    this.refs.track.addEventListener('pointerup', this.#onPointerUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#io?.disconnect();
    document.removeEventListener('visibilitychange', this.#onVisibility);
    this.refs.track?.removeEventListener('pointerdown', this.#onPointerDown);
    this.refs.track?.removeEventListener('pointerup', this.#onPointerUp);
    this.#clearExits();
    this.#pauseAll();
  }

  /* ---- Navegação (alvos dos on:click declarativos) ---- */

  /** @param {Event} [event] */
  next = (event) => {
    event?.preventDefault();
    this.select(this.#wrap(this.#active + 1));
  };

  /** @param {Event} [event] */
  previous = (event) => {
    event?.preventDefault();
    this.select(this.#wrap(this.#active - 1));
  };

  /**
   * Centraliza um card. Se já for o central, alterna o som (tap-to-unmute).
   * NÃO usar o nome `focus` (HTMLElement.prototype.focus já existe).
   * @param {number} index - vem como número de on:click="/select/{i}".
   * @param {Event} [event]
   */
  select(index, event) {
    event?.preventDefault();
    if (this.#suppressClick) {
      this.#suppressClick = false;
      return;
    }
    index = this.#wrap(Number(index));
    if (index === this.#active) {
      this.#toggleSound();
      return;
    }
    this.#active = index;
    this.#layout(true);
    this.#syncMedia();
  }

  /* ---- Anel (loop infinito sem clones) ---- */

  /** @param {number} i */
  #wrap(i) {
    const n = this.#count;
    return ((i % n) + n) % n;
  }

  /**
   * Distância assinada mais curta no anel (centro = 0, vizinhos ±1/±2…).
   * @param {number} i
   */
  #ringDelta(i) {
    const n = this.#count;
    let d = i - this.#active;
    if (d > n / 2) d -= n;
    if (d < -n / 2) d += n;
    return d;
  }

  /* ---- Layout ---- */

  /**
   * Reposiciona/redimensiona os cards via FLIP (First-Last-Invert-Play).
   *
   * O layout (largura/ordem) muda de forma INSTANTÂNEA — então a altura da
   * seção fica estável (o card central já entra na altura cheia, sem "dança").
   * A transição é só visual, via `transform` (translate + scale), que não causa
   * reflow. Sem FLIP, animar `width` fazia a altura (aspect-ratio 9/16) oscilar
   * durante o cruzamento dos cards.
   *
   * @param {boolean} animate
   */
  #layout(animate) {
    const { track, cards } = this.refs;
    // Finaliza saídas pendentes ANTES de medir: um card mid-saída está fora do
    // fluxo (absolute) e corromperia os rects do FLIP. Cliques rápidos cortam a
    // saída anterior — comportamento esperado.
    this.#clearExits();
    const reduce = prefersReducedMotion() || !animate;
    // FIRST: posição/tamanho atuais (visuais) antes de mudar.
    const first = reduce ? null : cards.map((card) => card.getBoundingClientRect());
    // LAST: aplica a nova ordem + distância (largura muda já, sem transição).
    for (const card of cards) {
      const i = Number(card.dataset.index);
      const delta = this.#ringDelta(i);
      card.dataset.distance = String(Math.min(3, Math.abs(delta)));
      card.style.order = String(delta);
    }
    this.#syncDots();
    if (!first) {
      // Sem animação: garante que nenhum transform/opacity residual fique aplicado.
      for (const card of cards) {
        card.style.transition = 'none';
        card.style.transform = 'none';
        card.style.opacity = '1';
        delete card.dataset.entering;
      }
      return;
    }
    // INVERT: aplica o transform que "desfaz" a mudança (volta ao estado antigo).
    cards.forEach((card, idx) => {
      const before = first[idx];
      const after = card.getBoundingClientRect();
      card.style.transition = 'none';
      // FLIP não interpola display:none <-> visível. Três casos sem "antes/depois":
      if (!before.width && !after.width) {
        // Escondido dos dois lados: nada a animar.
        card.style.transform = 'none';
        return;
      }
      if (!before.width) {
        // ENTRANDO na janela (vinha de display:none na extremidade). Sem posição
        // anterior p/ um FLIP, e com só 1 card reserva no anel deslizar "do outro
        // lado" cruzaria a tela. Animamos uma ENTRADA LOCAL: o card desliza ~40px
        // de fora pra dentro (na própria borda) e cresce, com fade-in junto.
        const dir = Math.sign(Number(card.style.order)); // +1 entra pela direita, -1 pela esquerda
        card.dataset.entering = '';
        card.style.opacity = '0';
        card.style.transform = `translateX(${dir * VD_EDGE_SHIFT}px) scale(0.9)`;
        return;
      }
      if (!after.width) {
        // SAINDO da janela (vai virar display:none). Espelho da entrada: tira do
        // fluxo, fixa na última posição e anima fade-out + deslize pra fora.
        this.#animateExit(card, before, track);
        return;
      }
      const dx = before.left + before.width / 2 - (after.left + after.width / 2);
      const scale = before.width / after.width; // aspect-ratio fixo -> escala uniforme
      card.style.transform = `translateX(${dx}px) scale(${scale})`;
    });
    // PLAY: força reflow e anima de volta ao estado final (transform: none).
    // Cards "entering" também transicionam a opacidade (fade-in da borda).
    void track.offsetWidth;
    for (const card of cards) {
      if (this.#exiting.has(card)) continue; // saída é controlada pela WAA, não pelo PLAY
      const entering = card.dataset.entering !== undefined;
      card.style.transition = entering ? `${VD_TRANSITION}, opacity ${VD_TIMING}` : VD_TRANSITION;
      card.style.transform = 'none';
      if (entering) {
        card.style.opacity = '1';
        delete card.dataset.entering;
      }
    }
  }

  /**
   * Anima a SAÍDA de um card que deixou a janela visível. Ele já recebeu
   * data-distance="3" (display:none), então o "resgatamos" fora do fluxo
   * (position:absolute) na sua última posição visível — assim os 5 que ficam
   * recentralizam sozinhos via flex sem um buraco — e fazemos fade-out + deslize
   * pra fora, na MESMA curva/duração do movimento. WAA porque ela se autolimpa
   * e não disputa o `transition` inline usado pelo FLIP.
   *
   * @param {HTMLElement} card
   * @param {DOMRect} before - rect visível imediatamente antes de sair.
   * @param {HTMLElement} track
   */
  #animateExit(card, before, track) {
    const trackRect = track.getBoundingClientRect();
    // Direção pela posição REAL anterior (não pelo order novo): no anel de 6 o
    // card que sai "embrulha" pro outro lado, então o order apontaria errado.
    const wasLeft = before.left + before.width / 2 < trackRect.left + trackRect.width / 2;
    const dir = wasLeft ? -1 : 1;
    card.style.position = 'absolute';
    card.style.margin = '0';
    card.style.left = `${before.left - trackRect.left}px`;
    card.style.top = `${before.top - trackRect.top}px`;
    card.style.width = `${before.width}px`;
    card.style.height = `${before.height}px`;
    card.style.display = 'block'; // inline vence o display:none do data-distance="3"
    card.style.zIndex = '5'; // acima dos 5 que recentralizam, p/ o fade-out ler limpo
    card.style.transition = 'none';
    card.style.transform = 'none';
    this.#exiting.add(card);
    const anim = card.animate(
      [
        { opacity: 1, transform: 'none' },
        { opacity: 0, transform: `translateX(${dir * VD_EDGE_SHIFT}px) scale(0.9)` },
      ],
      { duration: VD_DURATION, easing: VD_EASING, fill: 'forwards' }
    );
    this.#exitAnims.set(card, anim);
    anim.onfinish = () => this.#clearExit(card);
  }

  /** Finaliza a saída de UM card: cancela a animação e devolve ao estado normal
   *  (sem inline absolute), onde o data-distance="3" volta a escondê-lo. */
  #clearExit(card) {
    if (!this.#exiting.has(card)) return;
    this.#exiting.delete(card);
    const anim = this.#exitAnims.get(card);
    this.#exitAnims.delete(card);
    if (anim) {
      anim.onfinish = null;
      anim.cancel();
    }
    for (const prop of ['position', 'margin', 'left', 'top', 'width', 'height', 'display', 'z-index', 'transition', 'transform', 'opacity']) {
      card.style.removeProperty(prop);
    }
  }

  /** Finaliza TODAS as saídas pendentes (interrupção por nova navegação/disconnect). */
  #clearExits() {
    for (const card of [...this.#exiting]) this.#clearExit(card);
  }

  #syncDots() {
    this.refs.dotBtns?.forEach((dot, i) => {
      dot.setAttribute('aria-current', String(i === this.#active));
    });
  }

  /* ---- Mídia ---- */

  /** @param {HTMLElement} card */
  #video(card) {
    return /** @type {HTMLVideoElement | null} */ (card.querySelector('video'));
  }

  /** @param {HTMLElement} card */
  #soundBtn(card) {
    return card.querySelector('.video-depoimentos-meno__sound');
  }

  /**
   * Tenta tocar o vídeo e registra o resultado. Se o navegador rejeitar o
   * autoplay (ex.: `NotAllowedError` no iOS Safari com Modo de Baixo Consumo
   * ou Modo de Dados Reduzidos, que bloqueiam autoplay iniciado por script
   * mesmo com o vídeo mudo), o vídeo entra em `#autoplayBlocked` pra ser
   * tentado de novo no próximo gesto real do usuário (ver `#onPointerUp`) —
   * um `play()` disparado dentro de um gesto explícito passa pela política
   * de autoplay mesmo quando a tentativa automática foi bloqueada.
   * @param {HTMLVideoElement} video
   */
  #attemptPlay(video) {
    video.play().then(
      () => {
        this.#autoplayBlocked.delete(video);
      },
      () => {
        this.#autoplayBlocked.add(video);
        if (!this.#autoplayWarned) {
          this.#autoplayWarned = true;
          console.warn(
            '[video-depoimentos] autoplay bloqueado pelo navegador; será tentado de novo no próximo toque do usuário no carrossel.'
          );
        }
      }
    );
  }

  /** Aplica play/pause/mudo de acordo com quem é o card central. */
  #syncMedia() {
    this.refs.cards.forEach((card, i) => {
      const video = this.#video(card);
      const isActive = i === this.#active;
      card.classList.toggle('is-active', isActive);
      const btn = this.#soundBtn(card);
      if (btn) {
        btn.toggleAttribute('hidden', !isActive);
        btn.classList.remove('is-unmuted');
        btn.setAttribute('aria-label', 'Ativar som');
      }
      if (!video) return;
      if (isActive) {
        video.muted = true; // sempre (re)entra mudo
        const canPlay = this.#inView && !document.hidden && !prefersReducedMotion();
        if (canPlay) {
          this.#attemptPlay(video);
        } else {
          video.pause();
        }
      } else {
        video.pause();
        video.muted = true;
        this.#autoplayBlocked.delete(video);
      }
    });
  }

  /** Alterna o som do card central (gesto explícito do usuário). */
  #toggleSound() {
    const card = this.refs.cards[this.#active];
    const video = this.#video(card);
    if (!video) return;
    video.muted = !video.muted;
    if (!video.muted) this.#attemptPlay(video);
    const btn = this.#soundBtn(card);
    if (btn) {
      const unmuted = !video.muted;
      btn.classList.toggle('is-unmuted', unmuted);
      btn.setAttribute('aria-label', unmuted ? 'Silenciar' : 'Ativar som');
    }
  }

  #pauseAll() {
    this.refs.cards?.forEach((card) => {
      const video = this.#video(card);
      if (video) {
        video.pause();
        video.muted = true;
      }
    });
    this.#autoplayBlocked.clear();
  }

  /* ---- Eventos ---- */

  #onVisibility = () => {
    if (document.hidden) this.#pauseAll();
    else this.#syncMedia();
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    this.#pointerStartX = event.clientX;
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    // Qualquer toque no carrossel é um gesto real do usuário — aproveita pra
    // tentar de novo o autoplay do card central, caso tenha sido bloqueado
    // (ex.: iOS Safari em Modo de Baixo Consumo/Dados Reduzidos).
    const activeVideo = this.#video(this.refs.cards[this.#active]);
    if (activeVideo && this.#autoplayBlocked.has(activeVideo) && activeVideo.paused) {
      this.#attemptPlay(activeVideo);
    }
    if (this.#pointerStartX == null) return;
    const dx = event.clientX - this.#pointerStartX;
    this.#pointerStartX = null;
    if (Math.abs(dx) < 40) return;
    this.#suppressClick = true; // não deixar o clique pós-swipe alternar o som
    if (dx < 0) this.next();
    else this.previous();
  };
}

if (!customElements.get('video-depoimentos')) {
  customElements.define('video-depoimentos', VideoDepoimentos);
}
